import { FieldErrors, FieldValues } from 'react-hook-form'

export type QueryError = {
  message: string,
  errors: Record<string, string[]>,
}

const DEFAULT_ERROR = {
  message: 'An unexpected error occurred',
  errors: {},
}

export class QueryErrorHelper {
  static getQueryError(error: unknown): QueryError {
    if (!error) {
      return DEFAULT_ERROR
    }

    // Handle plain objects with message and errors properties
    if (typeof error === 'object' && error !== null && 'message' in error && 'errors' in error) {
      const errorObj = error as { message: string; errors: Record<string, string | string[]> }

      const errorObject: QueryError = {
        message: errorObj.message || DEFAULT_ERROR.message,
        errors: {},
      }

      // Ensure all error values are arrays
      for (const errorField in errorObj.errors) {
        const errorFieldValue = errorObj.errors[errorField]

        if (Array.isArray(errorFieldValue)) {
          errorObject.errors[errorField] = errorFieldValue
          continue
        }

        errorObject.errors[errorField] = [errorFieldValue]
      }

      return errorObject
    }

    if (error instanceof Error) {
      const parsedError = JSON.parse(error.message)

      const errorObject = {
        message: DEFAULT_ERROR.message,
        errors: {},
      }

      if ('message' in parsedError) {
        errorObject.message = parsedError.message
      }

      if ('errors' in parsedError) {
        for (const errorField in parsedError.errors) {
          const errorFieldValue = parsedError.errors[errorField]

          if (!Array.isArray(errorFieldValue)) {
            parsedError.errors[errorField] = [errorFieldValue]
          }
        }

        errorObject.errors = parsedError.errors
      }

      return errorObject
    }

    return DEFAULT_ERROR
  }

  static getQueryErrorByCode(error: unknown, code: string): QueryError | null {
    if (!error) {
      return null
    }

    // Handle plain objects with code and message properties
    if (typeof error === 'object' && 'code' in error && 'message' in error) {
      const errorObj = error as { code: string; message: string }
      if (errorObj.code === code) {
        return {
          message: errorObj.message,
          errors: {},
        }
      }
    }

    if (error instanceof Error) {
      const parsedError = JSON.parse(error.message)

      if ('code' in parsedError && parsedError.code === code) {
        return parsedError
      }
    }

    return null
  }

  static getQueryValidationErrors<TValues extends FieldValues>(error: unknown): FieldErrors<TValues> {
    const queryError = QueryErrorHelper.getQueryError(error)

    return Object.fromEntries(
      Object.entries(queryError.errors).map(([key, value]) => [key, {
        message: value[0],
      }]),
    ) as FieldErrors<TValues>
  }
}
